/* eslint-disable @angular-eslint/directive-selector */
import {
  Directive,
  ElementRef,
  inject,
  input,
  output,
  PLATFORM_ID,
  OnDestroy,
  effect,
  afterNextRender, // ✅ 추가
} from '@angular/core';
import Swiper from 'swiper';
import {
  Autoplay,
  Grid,
  Navigation,
  Pagination,
  Scrollbar,
  Mousewheel,
} from 'swiper/modules';
import type { SwiperOptions } from 'swiper/types';

@Directive({
  selector: '[swiper]',
  standalone: true,
})
export class SwiperDirective implements OnDestroy {
  // ✅ AfterViewInit 제거
  private readonly el = inject(ElementRef<HTMLElement>);
  private readonly platformId = inject(PLATFORM_ID);
  private swiper?: Swiper;

  options = input<SwiperOptions>();
  autoUpdateNav = input<boolean>(false);

  init = output<Swiper>();
  realIndex = output<number>();
  timeLeft = output<number>();

  private prevEl: HTMLElement | null = null;
  private nextEl: HTMLElement | null = null;

  constructor() {
    afterNextRender(() => {
      if (!this.options()) return;
      this.initializeSwiper(); // ✅ 레이아웃 완료 후 초기화
    });

    effect(() => {
      const opts = this.options();
      if (this.swiper && opts) {
        this.updateSwiperParams(opts);
        this.swiper.update();
      }
    });
  }

  private initializeSwiper(): void {
    const element = this.el.nativeElement;
    const options = this.options()!;

    this.setupUniqueId(element);
    this.setupNavigation(element, options);

    this.swiper = new Swiper(element, {
      modules: [Autoplay, Pagination, Grid, Navigation, Scrollbar, Mousewheel],
      ...options,
      on: this.getSwiperCallbacks(),
    });

    if (this.autoUpdateNav()) {
      this.updateNavVisibility();
    }

    this.init.emit(this.swiper);
  }

  private setupUniqueId(element: HTMLElement): void {
    if (!element.id) {
      element.id = `swiper-${Math.random().toString(36).substring(2, 8)}`;
    }
  }

  private setupNavigation(element: HTMLElement, options: SwiperOptions): void {
    if (!options.navigation) return;

    this.nextEl = element.querySelector('.swiper-button-next');
    this.prevEl = element.querySelector('.swiper-button-prev');

    if (this.nextEl) this.nextEl.id = `next-${element.id}`;
    if (this.prevEl) this.prevEl.id = `prev-${element.id}`;

    options.navigation = {
      nextEl: `#next-${element.id}`,
      prevEl: `#prev-${element.id}`,
    };
  }

  private getSwiperCallbacks() {
    return {
      realIndexChange: () => {
        if (this.autoUpdateNav()) {
          this.updateNavVisibility();
        }
        this.realIndex.emit(this.swiper?.realIndex ?? 0);
      },
      autoplayTimeLeft: () => {
        const time = this.swiper?.autoplay?.timeLeft ?? 0;
        this.timeLeft.emit(time);
      },
    };
  }

  private updateSwiperParams(opts: SwiperOptions): void {
    if (!this.swiper) return;

    if (opts.slidesPerView !== undefined) {
      this.swiper.params.slidesPerView = opts.slidesPerView;
    }
    if (opts.spaceBetween !== undefined) {
      this.swiper.params.spaceBetween = opts.spaceBetween;
    }
  }

  private updateNavVisibility(): void {
    if (!this.swiper || !this.prevEl || !this.nextEl) return;

    const { slides, realIndex, params } = this.swiper;
    const total = slides.length;
    const slidesPerView =
      typeof params.slidesPerView === 'number' ? params.slidesPerView : 1;

    const showPrev = total > 1 && realIndex > 0;
    const showNext = total > 1 && realIndex + slidesPerView < total;

    this.prevEl.style.display = showPrev ? 'block' : 'none';
    this.nextEl.style.display = showNext ? 'block' : 'none';
  }

  ngOnDestroy(): void {
    this.swiper?.destroy(true, true);
    this.swiper = undefined;
  }
}
